import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiTrendingUp,
  FiStar,
  FiZap,
  FiClock,
  FiMessageSquare,
  FiHash,
  FiRefreshCw,
  FiAlertCircle,
  FiLoader,
  FiChevronDown,
  FiMessageCircle,
  FiAward,
  FiUsers,
  FiGlobe,
  FiX,
} from "react-icons/fi";
import {
  useUnifiedFeed,
  useThreadVote,
  useThreadDownvote,
  useSavedThreadIds,
  useUnsaveThread,
} from "../../../services/communities";
import ThreadCard from "../threads/ThreadCard";
import SaveThreadModal from "../feed/SaveThreadModal";
import "./FeedSection.css";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

const fadeInVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.25 },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.15 },
  },
};

// Sort options configuration
const SORT_OPTIONS = [
  {
    id: "hot",
    label: "Hot",
    icon: FiTrendingUp,
    color: "#ef4444",
    description: "Most engaging right now",
  },
  {
    id: "best",
    label: "Best",
    icon: FiStar,
    color: "#f59e0b",
    description: "Top rated of all time",
  },
  {
    id: "top",
    label: "Top",
    icon: FiAward,
    color: "#8b5cf6",
    description: "Highest voted",
    hasTimeFilter: true,
  },
  {
    id: "new",
    label: "New",
    icon: FiClock,
    color: "#10b981",
    description: "Latest posts",
  },
  {
    id: "rising",
    label: "Rising",
    icon: FiZap,
    color: "#06b6d4",
    description: "Gaining traction",
  },
  {
    id: "controversial",
    label: "Controversial",
    icon: FiMessageSquare,
    color: "#ec4899",
    description: "Most debated",
    hasTimeFilter: true,
  },
];

const TIME_OPTIONS = [
  { id: "hour", label: "Past Hour" },
  { id: "day", label: "Today" },
  { id: "week", label: "This Week" },
  { id: "month", label: "This Month" },
  { id: "year", label: "This Year" },
  { id: "all", label: "All Time" },
];

const POPULAR_TAGS = [
  "research",
  "discussion",
  "question",
  "collaboration",
  "announcement",
  "help",
];

/**
 * FeedSection - Modern community feed with sorting options
 * Now supports URL-based tag filtering from sidebar trending topics
 */
const FeedSection = ({ searchQuery = "" }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Feed state
  const [sort, setSort] = useState("hot");
  const [filter, setFilter] = useState("all");
  const [time, setTime] = useState("week");
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);

  // Use URL tag as single source of truth for trending topics sidebar integration
  const tags = searchParams.get("tag") || "";
  
  // Helper to update tag - updates URL which causes re-render
  const setTags = useCallback((newTag) => {
    if (newTag) {
      setSearchParams({ tag: newTag });
    } else {
      setSearchParams({});
    }
  }, [setSearchParams]);

  // UI state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [selectedThreadForSave, setSelectedThreadForSave] = useState(null);

  // Refs
  const loadMoreRef = useRef(null);
  const timeDropdownRef = useRef(null);

  // Fetch feed data
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useUnifiedFeed(sort, filter, tags, time);

  // Fetch saved thread IDs for instant saved status
  const { data: savedData } = useSavedThreadIds();

  // Mutations
  const voteThreadMutation = useThreadVote();
  const downvoteThreadMutation = useThreadDownvote();
  const unsaveThreadMutation = useUnsaveThread();

  // Flatten paginated data and enhance with saved status
  const threads = useMemo(() => {
    const rawThreads = data?.pages?.flatMap((page) => page.threads) || [];
    
    // Enhance threads with isSaved status:
    // 1. Use local cache Set if available (best performance)
    // 2. Fallback to userHasSaved from backend response
    return rawThreads.map((thread) => ({
      ...thread,
      isSaved: savedData?.savedSet?.has(thread._id) ?? thread.userHasSaved ?? false,
    }));
  }, [data?.pages, savedData]);
  
  const algorithm = data?.pages?.[0]?.algorithm;
  const totalCommunities = data?.pages?.[0]?.totalCommunities;

  // Check if current sort has time filter
  const currentSortOption = SORT_OPTIONS.find((opt) => opt.id === sort);
  const showTimeFilter = currentSortOption?.hasTimeFilter;

  // Handle sort change
  const handleSortChange = useCallback((newSort) => {
    setSort(newSort);
    setShowTimeDropdown(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Handle voting
  const handleVote = useCallback(
    (threadId) => {
      const thread = threads.find((t) => t._id === threadId);
      if (!thread) return;

      voteThreadMutation.mutate({
        communityId: thread.community._id,
        threadId,
      });
    },
    [threads, voteThreadMutation]
  );

  const handleDownvote = useCallback(
    (threadId) => {
      const thread = threads.find((t) => t._id === threadId);
      if (!thread) return;

      downvoteThreadMutation.mutate({
        communityId: thread.community._id,
        threadId,
      });
    },
    [threads, downvoteThreadMutation]
  );

  // Handle save/unsave toggle
  const handleSave = useCallback(
    async (thread) => {
      if (thread.isSaved) {
        // Thread is already saved - unsave it directly (no modal needed)
        try {
          await unsaveThreadMutation.mutateAsync({ threadId: thread._id });
        } catch (error) { /* ignored */ }
      } else {
        // Thread is not saved - open modal for collection selection
        setSelectedThreadForSave(thread);
        setShowSaveModal(true);
      }
    },
    [unsaveThreadMutation]
  );

  // Handle thread click
  const handleThreadClick = useCallback(
    (thread) => {
      navigate(`/communities/${thread.community._id}/threads/${thread._id}`);
    },
    [navigate]
  );

  // Handle community click - navigate to community page
  const handleCommunityClick = useCallback(
    (community) => {
      navigate(`/communities/${community._id}`);
    },
    [navigate]
  );

  // Close time dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        timeDropdownRef.current &&
        !timeDropdownRef.current.contains(e.target)
      ) {
        setShowTimeDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Infinite scroll observer
  useEffect(() => {
    if (!loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1, rootMargin: "400px" }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Format time ago
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d`;

    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <div className="chub-feed">
      {/* Sorting Bar */}
      <motion.div
        className="chub-feed-controls"
        variants={fadeInVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Sort Pills */}
        <div className="chub-sort-pills">
          {SORT_OPTIONS.map((option) => {
            const Icon = option.icon;
            const isActive = sort === option.id;

            return (
              <motion.button
                key={option.id}
                className={`chub-sort-pill ${isActive ? "active" : ""}`}
                onClick={() => handleSortChange(option.id)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  "--pill-color": option.color,
                }}
              >
                <Icon size={15} />
                <span>{option.label}</span>
              </motion.button>
            );
          })}

          {/* Time Filter Dropdown */}
          <AnimatePresence>
            {showTimeFilter && (
              <motion.div
                className="chub-time-filter"
                ref={timeDropdownRef}
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
              >
                <button
                  className="chub-time-trigger"
                  onClick={() => setShowTimeDropdown(!showTimeDropdown)}
                >
                  <span>{TIME_OPTIONS.find((t) => t.id === time)?.label}</span>
                  <FiChevronDown
                    size={14}
                    className={showTimeDropdown ? "rotated" : ""}
                  />
                </button>

                <AnimatePresence>
                  {showTimeDropdown && (
                    <motion.div
                      className="chub-time-dropdown"
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                    >
                      {TIME_OPTIONS.map((option) => (
                        <button
                          key={option.id}
                          className={`chub-time-option ${
                            time === option.id ? "active" : ""
                          }`}
                          onClick={() => {
                            setTime(option.id);
                            setShowTimeDropdown(false);
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Divider */}
        <div className="chub-controls-divider" />

        {/* Feed Filter & Tags */}
        <div className="chub-feed-filters">
          {/* Joined/All Toggle */}
          <div className="chub-filter-toggle">
            <button
              className={`chub-toggle-btn ${
                filter === "joined" ? "active" : ""
              }`}
              onClick={() => setFilter("joined")}
            >
              <FiUsers size={14} />
              Joined
            </button>
            <button
              className={`chub-toggle-btn ${filter === "all" ? "active" : ""}`}
              onClick={() => setFilter("all")}
            >
              <FiGlobe size={14} />
              All
            </button>
          </div>

          {/* Tags */}
          <div className="chub-tags-scroll chub-hide-scrollbar">
            <button
              className={`chub-tag ${tags === "" ? "active" : ""}`}
              onClick={() => setTags("")}
            >
              All Topics
            </button>
            {POPULAR_TAGS.map((tag) => (
              <button
                key={tag}
                className={`chub-tag ${tags === tag ? "active" : ""}`}
                onClick={() => setTags(tag)}
              >
                <FiHash size={12} />
                {tag}
              </button>
            ))}
            
            {/* Show custom tag from sidebar if not in POPULAR_TAGS */}
            {tags && !POPULAR_TAGS.includes(tags) && (
              <button
                className="chub-tag active chub-tag-custom"
                onClick={() => setTags("")}
              >
                <FiHash size={12} />
                {tags}
                <FiX size={12} className="chub-tag-clear" />
              </button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Feed Info */}
      {algorithm && threads.length > 0 && (
        <motion.div
          className="chub-feed-info"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <span className="chub-feed-meta">
            <strong>{threads.length}</strong> threads
            {totalCommunities && ` from ${totalCommunities} communities`}
            <span className="chub-feed-algo">• Sorted by {algorithm}</span>
          </span>
          <button className="chub-refresh-btn" onClick={() => refetch()}>
            <FiRefreshCw size={14} />
            Refresh
          </button>
        </motion.div>
      )}

      {/* Threads List */}
      <div className="chub-feed-list">
        {isLoading ? (
          <motion.div
            className="chub-feed-state"
            variants={fadeInVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="chub-loader">
              <FiLoader className="chub-animate-spin" size={32} />
            </div>
            <h3>Loading your feed...</h3>
            <p>Fetching the latest discussions</p>
          </motion.div>
        ) : isError ? (
          <motion.div
            className="chub-feed-state error"
            variants={fadeInVariants}
            initial="hidden"
            animate="visible"
          >
            <FiAlertCircle size={40} />
            <h3>Something went wrong</h3>
            <p>{error?.message || "Failed to load feed"}</p>
            <button
              className="chub-btn chub-btn-retry"
              onClick={() => refetch()}
            >
              <FiRefreshCw size={16} />
              Try Again
            </button>
          </motion.div>
        ) : threads.length === 0 ? (
          <motion.div
            className="chub-feed-state empty"
            variants={fadeInVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="chub-empty-icon">
              <FiMessageCircle size={40} />
            </div>
            <h3>No threads yet</h3>
            <p>
              {filter === "joined"
                ? "Join some communities to see threads in your feed"
                : "No threads match your current filters"}
            </p>
          </motion.div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {threads.map((thread) => (
              <motion.div
                key={thread._id}
                variants={itemVariants}
                className="chub-thread-wrapper"
              >
                <ThreadCard
                  thread={thread}
                  onThreadClick={handleThreadClick}
                  onCommunityClick={handleCommunityClick}
                  onVote={handleVote}
                  onDownvote={handleDownvote}
                  onSave={handleSave}
                  showCommunityInfo={true}
                  formatTimeAgo={formatTimeAgo}
                />
              </motion.div>
            ))}

            {/* Load More Trigger */}
            <div ref={loadMoreRef} className="chub-load-more">
              {isFetchingNextPage ? (
                <div className="chub-loading-more">
                  <FiLoader className="chub-animate-spin" size={20} />
                  <span>Loading more...</span>
                </div>
              ) : hasNextPage ? (
                <span className="chub-scroll-hint">Scroll for more</span>
              ) : threads.length > 5 ? (
                <span className="chub-feed-end">You've reached the end!</span>
              ) : null}
            </div>
          </motion.div>
        )}
      </div>

      {/* Save Thread Modal */}
      <AnimatePresence>
        {showSaveModal && selectedThreadForSave && (
          <SaveThreadModal
            thread={selectedThreadForSave}
            onClose={() => {
              setShowSaveModal(false);
              setSelectedThreadForSave(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default FeedSection;
